// ===================================================
// ФАЙЛ: frontend/src/components/Error/MonumentErrorLayout.jsx
// Стили: STYLESYSTEM/interfaces/errors/error-pages.css (классы mb-err-page*)
// ===================================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { platformErrorIllustrationUrl } from '../../utils/platformAssets';

/** Коды с реальными JPG в public/assets/errors и /api/platform-assets/errors */
const MANIFEST_ASSET_CODES = new Set(['401', '403', '404', '429', '500', '503']);

/** Устаревшие алиасы → код ассета (на случай вызовов вне BlockingState) */
const BG_ALIAS_TO_CODE = {
  company_required: '403',
  verification_required: '403',
  agreement_required: '403',
  security_unavailable: '503',
};

function resolveErrorAssetCode(bgImageName, status) {
  const name = String(bgImageName || '').trim();
  if (MANIFEST_ASSET_CODES.has(name)) return name;
  if (BG_ALIAS_TO_CODE[name]) return BG_ALIAS_TO_CODE[name];
  const st = String(status || '').trim();
  if (MANIFEST_ASSET_CODES.has(st)) return st;
  return name || '404';
}

/** Тема иллюстрации: dark | white (суффикс файла). Ошибки — dark по умолчанию (кибердеревня). */
function resolveInitialErrorTheme() {
  if (typeof document === 'undefined') return 'dark';
  const dataTheme = document.documentElement.getAttribute('data-theme');
  if (dataTheme === 'dark') return 'dark';
  if (dataTheme === 'light' || dataTheme === 'white') return 'white';
  return 'dark';
}

const MonumentErrorLayout = ({
  status = '404',
  title = 'Страница не найдена',
  description = 'Кажется, эта тропинка в кибердеревне ещё не проложена.',
  actionText = 'Вернуться назад',
  actionLink = '/dashboard',
  bgImageName = '404',
  customAction = null,
  /** Внутри маркетинговой оболочки (шапка/подвал): не дублировать высоту viewport */
  fillParent = false,
  children
}) => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(resolveInitialErrorTheme);

  const assetCode = useMemo(
    () => resolveErrorAssetCode(bgImageName, status),
    [bgImageName, status]
  );

  const primaryBgUrl = platformErrorIllustrationUrl(assetCode, theme === 'dark');
  const fallbackBgUrl = `/assets/errors/${assetCode}_${theme}.jpg`;

  const [hasBg, setHasBg] = useState(false);
  const [activeBgUrl, setActiveBgUrl] = useState(null);
  /** null — манифест ещё не загружен; после загрузки — список имён, для которых реально лежат jpg */
  const [bgManifest, setBgManifest] = useState(null);

  useEffect(() => {
    // Известные коды (401/403/…) — грузим сразу, без манифеста (force-cache мог отдать пустой/битый ответ).
    if (MANIFEST_ASSET_CODES.has(String(assetCode))) {
      setBgManifest([...MANIFEST_ASSET_CODES]);
      return undefined;
    }
    let cancelled = false;
    fetch('/assets/errors/manifest.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { include: [] }))
      .then((m) => {
        const list = Array.isArray(m?.include) ? m.include.map(String) : [];
        if (!cancelled) setBgManifest(list);
      })
      .catch(() => {
        if (!cancelled) setBgManifest([]);
      });
    return () => { cancelled = true; };
  }, [assetCode]);

  useEffect(() => {
    if (bgManifest === null) return;
    const allowed = bgManifest.includes(String(assetCode)) || MANIFEST_ASSET_CODES.has(String(assetCode));
    if (!allowed) {
      setHasBg(false);
      setActiveBgUrl(null);
      return;
    }
    let cancelled = false;
    const tryUrl = (url, onFail) => {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setActiveBgUrl(url);
        setHasBg(true);
      };
      img.onerror = () => {
        if (cancelled) return;
        onFail();
      };
      img.src = url;
    };
    tryUrl(primaryBgUrl, () => {
      tryUrl(fallbackBgUrl, () => {
        setActiveBgUrl(null);
        setHasBg(false);
      });
    });
    return () => { cancelled = true; };
  }, [primaryBgUrl, fallbackBgUrl, bgManifest, assetCode]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'white' : 'dark');
  };

  const handleAction = () => {
    if (customAction) {
      customAction();
    } else if (actionLink) {
      navigate(actionLink);
    }
  };

  const rootClass = [
    'mb-err-page',
    fillParent ? 'mb-err-page--fill-parent' : '',
    theme === 'dark' ? 'mb-err-page--dark' : '',
    hasBg ? 'mb-err-page--has-bg' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass} role="main" aria-labelledby="mb-err-page-status">
      <div
        className="mb-err-page__bg"
        aria-hidden="true"
        style={
          hasBg && activeBgUrl
            ? { '--mb-err-page-bg-image': `url("${String(activeBgUrl).replace(/"/g, '')}")` }
            : undefined
        }
      />

      {process.env.NODE_ENV === 'development' && (
        <button
          type="button"
          onClick={toggleTheme}
          className="mb-err-page__dev-theme-toggle"
          title="Переключить тему для теста"
          aria-label="Переключить светлую и тёмную тему предпросмотра фона"
        >
          {theme === 'dark' ? <Sun size={18} aria-hidden /> : <Moon size={18} aria-hidden />}
        </button>
      )}

      <div className="mb-err-page__panel">
        <h1 id="mb-err-page-status" className="mb-err-page__code">
          {status}
        </h1>
        <h2 className="mb-err-page__title">{title}</h2>
        <p className="mb-err-page__text">{description}</p>

        {children ? (
          <div className="mb-err-page__slot">
            {children}
          </div>
        ) : (
          <button type="button" className="mb-err-page__cta" onClick={handleAction}>
            {actionText}
          </button>
        )}
      </div>
    </div>
  );
};

export default MonumentErrorLayout;
